import {
  Badge,
  Box,
  Flex,
  Grid,
  Heading,
  Spinner,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { FiArrowLeft, FiInfo, FiTag } from "react-icons/fi"

import { OpenAPI, TagTemplatesService } from "@/client"
import type { TagTemplateRead, TagTypeEnum } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import { useOrganization } from "@/contexts/OrganizationContext"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { TAG_TYPE_ORDER, getTagTypeMeta } from "@/lib/tagTypeMeta"

export const Route = createFileRoute("/_layout/tag-types")({
  component: TagTypesPage,
})

// Lightweight tag shape from the /paginated endpoint — it already
// carries the three fields we chain on (tag_type + category + sku).
interface PaginatedTagRow {
  id: string
  name: string
  epcid: string
  sku?: string | null
  category?: string | null
  tag_type?: TagTypeEnum | null
  tag_template_id?: string | null
}

function authedFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  })
}

function TagTypesPage() {
  const sc = useSemanticColors()
  const { t } = useTranslation(["admin", "tags"])
  const navigate = useNavigate()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  // Operational templates — these carry tag_type DIRECTLY, so they map
  // 1:1 onto a tag type.
  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["tag-templates", orgId],
    queryFn: async (): Promise<TagTemplateRead[]> => {
      if (!orgId) return []
      try {
        return await TagTemplatesService.readOrganizationTemplates({
          organisationId: orgId,
          skip: 0,
          limit: 1000,
        })
      } catch {
        return []
      }
    },
    enabled: !!orgId,
    staleTime: 30_000,
  })

  // Tags carry tag_type + category + sku. Categories and SKUs are
  // free-text per-tag (no tag_type FK), so the only way to relate them
  // to a tag type is THROUGH the tags that use them.
  const { data: tags = [], isLoading: tagsLoading } = useQuery({
    queryKey: ["tag-types-tags", orgId],
    queryFn: async (): Promise<PaginatedTagRow[]> => {
      if (!orgId) return []
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/tags/paginated?limit=1000`,
      )
      if (!r.ok) throw new Error(await r.text())
      const body = (await r.json()) as { data: PaginatedTagRow[] }
      return body.data ?? []
    },
    enabled: !!orgId,
    staleTime: 30_000,
  })

  // Map a tag → its effective tag type: prefer the tag's own column,
  // fall back to the bound template's type (mirrors the resolution the
  // tag-detail page uses).
  const templateTypeById = useMemo(() => {
    const m = new Map<string, TagTypeEnum>()
    for (const tpl of templates) m.set(tpl.id, tpl.tag_type)
    return m
  }, [templates])

  const grouped = useMemo(() => {
    return TAG_TYPE_ORDER.map((type) => {
      const typeTemplates = templates.filter((tpl) => tpl.tag_type === type)
      const typeTags = tags.filter((tag) => {
        const effective =
          tag.tag_type ??
          (tag.tag_template_id
            ? templateTypeById.get(tag.tag_template_id)
            : undefined)
        return effective === type
      })
      const categories = Array.from(
        new Set(
          typeTags
            .map((tg) => tg.category?.trim())
            .filter((c): c is string => !!c),
        ),
      ).sort((a, b) => a.localeCompare(b))
      const skus = Array.from(
        new Set(
          typeTags.map((tg) => tg.sku?.trim()).filter((s): s is string => !!s),
        ),
      ).sort((a, b) => a.localeCompare(b))
      return {
        type,
        typeTemplates,
        tagCount: typeTags.length,
        categories,
        skus,
      }
    })
  }, [templates, tags, templateTypeById])

  const isLoading = templatesLoading || tagsLoading

  if (!orgId) {
    return (
      <Box>
        <Text color={sc.textMuted}>
          {t("common:org.selectPrompt", "Select an organisation to continue.")}
        </Text>
      </Box>
    )
  }

  return (
    <Box>
      <Flex mb={4} align="center" gap={3}>
        <VisionButton
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/admin-tags" })}
        >
          <FiArrowLeft />
          <Text ml={2}>{t("tagTypes.back", "Back to Tags")}</Text>
        </VisionButton>
      </Flex>

      <Flex align="center" gap={3} mb={4} flexWrap="wrap">
        <IconBox variant="glass" size="lg">
          <FiTag fontSize="24px" color={sc.iconColor} />
        </IconBox>
        <Box flex="1">
          <Heading size="lg" color={sc.heading}>
            {t("tagTypes.title", "Tag Types")}
          </Heading>
          <Text color={sc.textMuted}>
            {t(
              "tagTypes.subtitle",
              "The fixed catalog of tag types and everything bound to each.",
            )}
          </Text>
        </Box>
      </Flex>

      {/* Read-only notice — tag types are a backend enum, not editable. */}
      <VisionCard mb={6}>
        <Flex gap={3} p={4} align="flex-start">
          <Box mt="2px">
            <FiInfo fontSize="18px" color={sc.iconColor} />
          </Box>
          <Box>
            <Text color={sc.heading} fontWeight="600" fontSize="sm" mb={1}>
              {t("tagTypes.readOnlyTitle", "Tag types are read-only")}
            </Text>
            <Text color={sc.textMuted} fontSize="sm">
              {t(
                "tagTypes.readOnlyBody",
                "Tag types are defined in the backend code (TagTypeEnum in backend/app/models/tag.py). They cannot be added, renamed, or removed from the dashboard — changing the set of tag types requires a feature request to the development team.",
              )}
            </Text>
          </Box>
        </Flex>
      </VisionCard>

      {isLoading ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" color={sc.spinnerColor} />
        </Flex>
      ) : (
        <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={4}>
          {grouped.map(
            ({ type, typeTemplates, tagCount, categories, skus }) => {
              const meta = getTagTypeMeta(type)
              return (
                <VisionCard key={type}>
                  <Box p={4}>
                    {/* Header: icon + human label + raw enum value */}
                    <Flex align="center" gap={3} mb={4} flexWrap="wrap">
                      <Text fontSize="2xl">{meta.icon}</Text>
                      <Box flex="1" minW={0}>
                        <Heading size="md" color={sc.heading}>
                          {t(meta.labelKey, type)}
                        </Heading>
                        <Text
                          color={sc.textMuted}
                          fontSize="xs"
                          fontFamily="mono"
                        >
                          {type}
                        </Text>
                      </Box>
                      <Badge colorPalette={meta.color} size="sm">
                        {t("tagTypes.tagCount", "{{count}} tags", {
                          count: tagCount,
                        })}
                      </Badge>
                    </Flex>

                    {/* Chained: Templates (direct — carry tag_type) */}
                    <ChainSection
                      label={t("tagTypes.section.templates", "Tag Templates")}
                      count={typeTemplates.length}
                      sc={sc}
                    >
                      {typeTemplates.length === 0 ? (
                        <EmptyHint
                          sc={sc}
                          text={t("tagTypes.noTemplates", "No templates.")}
                        />
                      ) : (
                        <Flex direction="column" gap={1}>
                          {typeTemplates.map((tpl) => {
                            const matchHint =
                              tpl.chip_manufacturer ||
                              tpl.tag_type_match ||
                              null
                            return (
                              <Flex
                                key={tpl.id}
                                align="center"
                                gap={2}
                                cursor="pointer"
                                _hover={{ opacity: 0.8 }}
                                onClick={() =>
                                  navigate({
                                    to: "/template-form",
                                    search: { templateId: tpl.id },
                                  })
                                }
                              >
                                <Text fontSize="sm" color={sc.heading}>
                                  {tpl.name}
                                </Text>
                                {tpl.is_default && (
                                  <Badge colorPalette="blue" size="xs">
                                    {t("tagTypes.defaultBadge", "default")}
                                  </Badge>
                                )}
                                {matchHint && (
                                  <Badge
                                    colorPalette="gray"
                                    size="xs"
                                    variant="surface"
                                  >
                                    {matchHint}
                                  </Badge>
                                )}
                              </Flex>
                            )
                          })}
                        </Flex>
                      )}
                    </ChainSection>

                    {/* Chained: Categories (indirect — via tags of this type) */}
                    <ChainSection
                      label={t(
                        "tagTypes.section.categories",
                        "Categories in use",
                      )}
                      count={categories.length}
                      sc={sc}
                    >
                      {categories.length === 0 ? (
                        <EmptyHint
                          sc={sc}
                          text={t("tagTypes.noCategories", "None in use.")}
                        />
                      ) : (
                        <Flex gap={1} wrap="wrap">
                          {categories.map((c) => (
                            <Badge key={c} colorPalette="purple" size="sm">
                              {c}
                            </Badge>
                          ))}
                        </Flex>
                      )}
                    </ChainSection>

                    {/* Chained: SKU bindings (indirect — via tags of this type) */}
                    <ChainSection
                      label={t("tagTypes.section.skus", "SKU bindings in use")}
                      count={skus.length}
                      sc={sc}
                      last
                    >
                      {skus.length === 0 ? (
                        <EmptyHint
                          sc={sc}
                          text={t("tagTypes.noSkus", "None in use.")}
                        />
                      ) : (
                        <Flex gap={1} wrap="wrap">
                          {skus.map((s) => (
                            <Badge
                              key={s}
                              colorPalette="teal"
                              size="sm"
                              fontFamily="mono"
                            >
                              {s}
                            </Badge>
                          ))}
                        </Flex>
                      )}
                    </ChainSection>
                  </Box>
                </VisionCard>
              )
            },
          )}
        </Grid>
      )}
    </Box>
  )
}

// A labelled sub-section inside a tag-type card. Keeps the three chained
// blocks (templates / categories / SKUs) visually consistent.
function ChainSection({
  label,
  count,
  sc,
  last,
  children,
}: {
  label: string
  count: number
  sc: ReturnType<typeof useSemanticColors>
  last?: boolean
  children: React.ReactNode
}) {
  return (
    <Box
      pb={last ? 0 : 3}
      mb={last ? 0 : 3}
      borderBottom={last ? undefined : "1px solid"}
      borderColor="rgba(255,255,255,0.08)"
    >
      <Text
        fontSize="xs"
        color={sc.textMuted}
        textTransform="uppercase"
        letterSpacing="wider"
        mb={2}
      >
        {label} ({count})
      </Text>
      {children}
    </Box>
  )
}

function EmptyHint({
  sc,
  text,
}: {
  sc: ReturnType<typeof useSemanticColors>
  text: string
}) {
  return (
    <Text
      fontSize="sm"
      color={sc.textSubtle ?? sc.textMuted}
      fontStyle="italic"
    >
      {text}
    </Text>
  )
}
